"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { NPSForm } from './NPSForm/NPSForm.web.js';
import { ScorePage } from './Pages/ScorePage.web.js';
import { FeedbackPage } from './Pages/FeedbackPage.web.js';
import { ThankYouPage } from './Pages/ThankYouPage.web.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { callOptional } from '../utils/helpers/function.js';
import { useSmartControlledEventHandler } from '../utils/hooks/useSmartControlledEventHandler.web.js';

var _excluded = ["labels", "minScore", "maxScore", "score", "onSetScore", "onSubmitScore", "onClose", "feedbackText", "onFeedbackTextChange", "onSubmitFeedbackText", "surveyState", "autoAdvance", "disableSubmit", "error", "color"];
var defaultNPSLabels = {
  heading: 'Survey',
  npsDescription: 'How likely are you to recommend this service to a friend or colleague?',
  feedbackDescription: 'What is the primary reason for the score you submitted?',
  minScore: 'Very unlikely',
  maxScore: 'Very likely',
  feedbackLabel: 'Comment',
  thankYou: 'Thank you for your feedback!',
  submit: 'Submit',
  close: 'Close',
  error: 'Oops! Something went wrong!'
};
var NPS = function NPS(_ref) {
  var labels = _ref.labels,
    _ref$minScore = _ref.minScore,
    minScore = _ref$minScore === undefined ? 0 : _ref$minScore,
    _ref$maxScore = _ref.maxScore,
    maxScore = _ref$maxScore === undefined ? 10 : _ref$maxScore,
    scoreProp = _ref.score,
    onSetScore = _ref.onSetScore,
    onSubmitScore = _ref.onSubmitScore,
    onClose = _ref.onClose,
    feedbackTextProp = _ref.feedbackText,
    onFeedbackTextChange = _ref.onFeedbackTextChange,
    onSubmitFeedbackText = _ref.onSubmitFeedbackText,
    surveyStateProp = _ref.surveyState,
    _ref$autoAdvance = _ref.autoAdvance,
    autoAdvance = _ref$autoAdvance === undefined ? true : _ref$autoAdvance,
    _ref$disableSubmit = _ref.disableSubmit,
    disableSubmit = _ref$disableSubmit === undefined ? false : _ref$disableSubmit,
    _ref$error = _ref.error,
    error = _ref$error === undefined ? false : _ref$error,
    color = _ref.color,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlled = useSmartControlled('score', surveyStateProp),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    surveyState = _useSmartControlled2[0],
    setSurveyState = _useSmartControlled2[1];

  /* NPS score state */
  var _useSmartControlled3 = useSmartControlled(-1, scoreProp, onSetScore),
    _useSmartControlled4 = _slicedToArray(_useSmartControlled3, 2),
    score = _useSmartControlled4[0],
    setScore = _useSmartControlled4[1];
  var handleSubmitScore = React.useCallback(function () {
    callOptional(onSubmitScore)(score);
    if (autoAdvance) setSurveyState('feedback');
  }, [score, onSubmitScore, autoAdvance, setSurveyState]);

  /* Feedback text state */
  var _useSmartControlledEv = useSmartControlledEventHandler('', feedbackTextProp, onFeedbackTextChange),
    _useSmartControlledEv2 = _slicedToArray(_useSmartControlledEv, 2),
    feedbackText = _useSmartControlledEv2[0],
    handleFeedbackTextChange = _useSmartControlledEv2[1];
  var handleSubmitFeedbackText = React.useCallback(function () {
    callOptional(onSubmitFeedbackText)(feedbackText);
    if (autoAdvance) setSurveyState('thankyou');
  }, [feedbackText, onSubmitFeedbackText, autoAdvance, setSurveyState]);
  switch (surveyState) {
    case 'score':
      {
        return /*#__PURE__*/React.createElement(NPSForm, _extends({
          labels: labels,
          onClose: onClose,
          onSubmit: handleSubmitScore,
          disableSubmit: score < 0 || disableSubmit,
          error: error,
          color: color
        }, props), /*#__PURE__*/React.createElement(ScorePage, {
          labels: labels,
          minScore: minScore,
          maxScore: maxScore,
          score: score,
          setScore: setScore,
          color: color
        }));
      }
    case 'feedback':
      {
        return /*#__PURE__*/React.createElement(NPSForm, _extends({
          labels: labels,
          onClose: onClose,
          onSubmit: handleSubmitFeedbackText,
          disableSubmit: disableSubmit,
          error: error,
          color: color
        }, props), /*#__PURE__*/React.createElement(FeedbackPage, {
          labels: labels,
          onChange: handleFeedbackTextChange,
          value: feedbackText,
          color: color
        }));
      }
    case 'thankyou':
      {
        return /*#__PURE__*/React.createElement(NPSForm, _extends({
          labels: labels,
          onClose: onClose,
          onSubmit: onClose,
          submitCloses: true,
          color: color
        }, props), /*#__PURE__*/React.createElement(ThankYouPage, {
          labels: labels
        }));
      }
    default:
      throw new Error('Unknown surveyState passed to NPS');
  }
};

export { NPS, defaultNPSLabels };
